import { lazy } from 'react';
import type { BlockStateDefinition } from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import type {
  CopilotThreadBlockRef,
  CopilotThreadBlockStateType,
  CopilotThreadComponentType,
} from '@unifyapps/defs/blocks/CopilotThread/types';
import { CopilotThreadBlockMethods } from '@unifyapps/defs/blocks/CopilotThread/types';
import type { Action } from '@unifyapps/defs/types/action';
import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import { ENTIRE_ENTITY } from '@unifyapps/carbon/no-code/dependency/const';
import type { DependsOnType } from '@unifyapps/defs/types/page';

const importCopilotThreadComponent = () =>
  import(
    /* webpackChunkName: "copilot-thread-block" */
    './CopilotThread'
  );

const CopilotThread = lazy(importCopilotThreadComponent);

class CopilotThreadBlockStateDefinition implements BlockStateDefinition {
  useBlockRef?: boolean | undefined = true;

  get type(): 'CopilotThread' {
    return 'CopilotThread';
  }
  getSlotOrder(): { order: string[] } {
    return {
      order: ['copilotInputPanel'],
    };
  }

  get initialStateGetter() {
    return (block: BlockType<CopilotThreadComponentType>) =>
      ({
        id: block.id,
        ..._pick(block.component, ['content', 'appearance', 'componentType']),
      }) as CopilotThreadBlockStateType;
  }

  getImplicitDependencies(
    block: BlockType<CopilotThreadComponentType>,
  ): DependsOnType[] | undefined {
    const copilotInputPanelBlockId = block.component.slots?.copilotInputPanel?.blockId;
    if (!copilotInputPanelBlockId) return;

    return [
      {
        id: copilotInputPanelBlockId,
        p: [ENTIRE_ENTITY],
      },
    ];
  }

  getBlockControlMethods() {
    return [
      {
        id: CopilotThreadBlockMethods.SendMessage,
        label: 'Send Message',
        method: (block: BlockStateUnionType, action: Action, blockRef?: CopilotThreadBlockRef) => {
          const methodPayload = (action.payload?.methodPayload ?? {}) as {
            message?: string;
            attachments?: FileType[];
          };

          if (methodPayload.message || methodPayload.attachments?.length) {
            blockRef?.[CopilotThreadBlockMethods.SendMessage as const]?.({
              message: methodPayload.message,
              attachments: methodPayload.attachments,
            });

            return Promise.resolve();
          }

          return {
            ...block,
            errorMessage: 'Message cannot be empty',
          };
        },
      },
      {
        id: CopilotThreadBlockMethods.GoToThread,
        label: 'Go to thread',
        method: (_, action: Action, blockRef?: CopilotThreadBlockRef) => {
          const threadId = (action.payload?.methodPayload ?? '') as string;

          blockRef?.[CopilotThreadBlockMethods.GoToThread as const]?.(threadId);

          return Promise.resolve();
        },
      },
      {
        id: CopilotThreadBlockMethods.CreateNewThread,
        label: 'Create new thread',
        method: (_, __, blockRef?: CopilotThreadBlockRef) => {
          blockRef?.[CopilotThreadBlockMethods.CreateNewThread as const]?.();

          return Promise.resolve();
        },
      },
      {
        id: CopilotThreadBlockMethods.StopResponseGeneration,
        label: 'Stop Response Generation',
        method: (_, __, blockRef?: CopilotThreadBlockRef) => {
          blockRef?.[CopilotThreadBlockMethods.StopResponseGeneration as const]?.();

          return Promise.resolve();
        },
      },
    ];
  }
  getComponent() {
    return CopilotThread;
  }
}

export default CopilotThreadBlockStateDefinition;
